/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */
import {ActionTree, Commit} from 'vuex';
import {JsonLdCollection, RootState} from '@/store/types';
import {AxiosResponse} from 'axios';
import {api} from '@/services/http';
import paramsSerializer from '@/services/http/serializer/stringify';
import {CommunityChallengesStateInterface} from '@/store/modules/community-challenges/types';
import {CommunityChallengeInterface} from '@/store/modules/community-challenge/types';

/**
 * Actions for community challenges operations.
 *
 * Actions are functions that can be exported and called upon potentially in an asynchronous manner.
 * To add an action export a named const function. e.g. export const fnName = ({ commit }, {id, name}) => { ... }
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const actions: ActionTree<CommunityChallengesStateInterface, RootState> = {
    /**
     * Load community challenges.
     * @param {Commit} commit
     * @param {CommunityChallengesStateInterface} state
     * @param {object} params
     * @return {Promise<JsonLdCollection<CommunityChallengeInterface>>}
     */
    async getChallenges(
        {commit, state}: {commit: Commit, state: CommunityChallengesStateInterface},
        params: object = {},
    ): Promise<JsonLdCollection<CommunityChallengeInterface>> {
        try {
            commit('start');
            const response: AxiosResponse<JsonLdCollection<CommunityChallengeInterface>> = await api.get('api/zoo/challenges/history/community', {
                params: {...params},
                paramsSerializer,
            });

            commit('setChallenges', response.data);
            commit('stop', true);
            return response.data;
        } catch (e) {
            commit('stop', false);
            throw e;
        }
    },


    /**
     * Get the next set of challenges (if available).
     * @param {Commit} commit
     * @param {CommunityChallengesStateInterface} state
     * @param {object} params
     * @return {Promise<JsonLdCollection<CommunityChallengeInterface>>}
     */
    async getNext(
        {commit, state}: {commit: Commit, state: CommunityChallengesStateInterface},
        params: object = {},
    ): Promise<JsonLdCollection<CommunityChallengeInterface>|void> {
        if (undefined === state.pagination.next) {
            return;
        }

        return api.get(state.pagination.next, {
            params: {...params},
            paramsSerializer,
        }).then((response: AxiosResponse<JsonLdCollection<CommunityChallengeInterface>>) => {
            commit('setChallenges', response.data);
            return response.data;
        });
    },

    /**
     * Reset challenge list.
     * @param {Commit} commit
     */
    reset({commit}: {commit: Commit}): void {
        commit('reset');
    },
};
